import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CheckList,
  Checkbox,
  Dialog,
  Dropdown,
  DropdownRef,
  Selector,
  Empty,
  InfiniteScroll,
  Popup,
  SearchBar,
  SearchBarRef,
  Tabs,
  Tag,
} from 'antd-mobile';
import { CloseOutline, DownOutline } from 'antd-mobile-icons';
import styles from './index.module.scss';
import {
  DelayEnergyConsumptionResponse,
  getDelayEnergyConsumptionPage,
  getNumOfEachApprovalStatus,
  NumOfEachApprovalStatusResponse,
  getStaffDept,
  GetStaffDeptPostResponse,
  GetAreaListPostResponse,
  DelayEnergyConsumptionRequest,
  getAreaList,
  oneKeyApproval,
} from '@/api/energy';
import { isEmpty } from 'lodash-es';
import NoData from '@/assets/images/noData.png';
import {
  ApprovalStatusDisplay,
  ApprovalStatusColorDisplay,
  DelayTitleDisplay,
  DelayTitleEnum,
  ApprovalStatus,
} from './util';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import unActualApprovalImg from '../EnergyUseApplicationDetail/imgs/unActualApproval@2x.png';

const EnergyUseApplication = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DelayEnergyConsumptionResponse[]>([]);
  const [approvalCountInfo, setApprovalCountInfo] = useState<NumOfEachApprovalStatusResponse>();
  const [sectionOp, setSectionOp] = useState<GetStaffDeptPostResponse[]>([]);
  const [areaOp, setAreaOp] = useState<GetAreaListPostResponse[]>([]);
  const [selectedDept, setSelectedDept] = useState<number>();
  const [selectedArea, setSelectedArea] = useState<number[]>([]);
  const [searchParams, setSearchParams] = useState<DelayEnergyConsumptionRequest>({ page: 1, size: 20 });
  const [hasMore, setHasMore] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(DelayTitleEnum.ALL as unknown as string);
  const [total, setTotal] = useState(0);

  const [searchDeptText, setSearchDeptText] = useState('');
  const [searchAreaText, setSearchAreaText] = useState('');

  const ref = useRef<DropdownRef>(null);

  // 批量审批相关状态
  const [batchMode, setBatchMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const searchOwnerRef = useRef<SearchBarRef>(null);
  const searchZoneRef = useRef<SearchBarRef>(null);

  useEffect(() => {
    document.title = '延时申请';
  }, []);

  useEffect(() => {
    getStaffDept().then(res => {
      setSectionOp(res.list);
    });
  }, []);

  useEffect(() => {
    if (searchParams?.ownerOrgId) {
      getAreaList(searchParams?.ownerOrgId).then(res => {
        setAreaOp(res.list);
      });
    } else {
      setAreaOp([]);
    }
  }, [searchParams.ownerOrgId]);

  useEffect(() => {
    getDelayEnergyConsumptionPage({
      ...searchParams,
      status: activeTab ? Number(activeTab) : undefined,
    }).then(res => {
      if (searchParams.page === 1) {
        setData(res.list ?? []);
      } else {
        setData(prev => [...prev, ...(res.list ?? [])]);
      }
      setHasMore(res.list?.length > 0 && res.total > 20);
      setTotal(res.total ?? 0);
    });
    getNumOfEachApprovalStatus({
      ...searchParams,
      status: activeTab ? Number(activeTab) : undefined,
    }).then(res => {
      setApprovalCountInfo(res);
    });
  }, [searchParams, activeTab]);

  const filteredDeptItems = useMemo(() => {
    if (searchDeptText) {
      return sectionOp.filter(item => item.ownerOrgName!.includes(searchDeptText));
    } else {
      return sectionOp;
    }
  }, [sectionOp, searchDeptText]);
  const filteredAreaItems = useMemo(() => {
    if (searchAreaText) {
      return areaOp.filter(item => item.zoneName!.includes(searchAreaText));
    } else {
      return areaOp;
    }
  }, [areaOp, searchAreaText]);

  // 可审批的记录（待审批状态且approvable为true）
  const approvableItems = useMemo(() => {
    return data.filter(
      item =>
        (item.approvalResult === ApprovalStatus.PENDING_FIRST ||
          item.approvalResult === ApprovalStatus.PENDING_SECOND) &&
        item.approvable
    );
  }, [data]);

  // 是否显示批量审批按钮（有待审批的记录）
  const showBatchButton = useMemo(() => {
    return approvableItems.length > 0;
  }, [approvableItems]);

  // 全选状态：'all' | 'partial' | 'none'
  const selectAllStatus = useMemo(() => {
    if (selectedIds.length === 0) return 'none';
    if (selectedIds.length === approvableItems.length) return 'all';
    return 'partial';
  }, [selectedIds, approvableItems]);

  // 全选
  const handleSelectAll = () => {
    if (selectAllStatus === 'all') {
      setSelectedIds([]);
    } else {
      setSelectedIds(approvableItems.map(item => item.id!));
    }
  };

  // 切换批量模式
  const toggleBatchMode = () => {
    setBatchMode(!batchMode);
    setSelectedIds([]);
  };

  // 批量审批提交
  const handleBatchApproval = (result: number) => {
    const title = result === 0 ? '批量通过' : '批量驳回';
    Dialog.confirm({
      content: (
        <div style={{ textAlign: 'center' }}>
          确定{title}
          <span style={{ color: 'red' }}>{selectedIds.length}项</span>延时申请？
        </div>
      ),
      confirmText: '确定',
      onConfirm: async () => {
        // 获取选中记录的审批层级

        await oneKeyApproval({
          ids: selectedIds,
          approvalType: result,
        });

        setBatchMode(false);
        setSelectedIds([]);
        // 刷新列表
        setSearchParams(prev => ({ ...prev, page: 1 }));
      },
    });
  };

  const loadMore = async () => {
    const nextPage = searchParams.page! + 1;
    setSearchParams(prev => ({ ...prev, page: nextPage }));
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setData([]);
    setHasMore(false);
    setSearchParams(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div className={styles.page}>
      {/* 批量审批模式时隐藏头部筛选和Tabs */}
      {!batchMode && (
        <div className={styles.header}>
          <Dropdown
            ref={ref}
            onChange={key => {
              if (key) {
                setSelectedDept(searchParams.ownerOrgId);
                setSelectedArea(searchParams.zoneIdList || []);
              } else {
                setSearchAreaText('');
                setSearchDeptText('');
                searchOwnerRef.current?.clear();
                searchZoneRef.current?.clear();
              }
            }}
          >
            <Dropdown.Item
              key="ownerOrgId"
              title="部门"
              style={{ color: searchParams!.ownerOrgId ? 'var(--adm-color-primary)' : '' }}
            >
              <div className={styles.dropdownPanel}>
                <SearchBar placeholder="请输入部门名称" ref={searchOwnerRef} onChange={setSearchDeptText} />
                {!isEmpty(filteredDeptItems) ? (
                  <CheckList
                    value={selectedDept ? [selectedDept] : []}
                    onChange={val => {
                      setSelectedDept(val[0] as number);
                    }}
                  >
                    {filteredDeptItems?.map(item => (
                      <CheckList.Item key={item.ownerOrgId} value={item.ownerOrgId!}>
                        {item.ownerOrgName}
                      </CheckList.Item>
                    ))}
                  </CheckList>
                ) : (
                  <div className={styles.emptyState}>
                    <Empty image={NoData} description="暂无数据" />
                  </div>
                )}
                <div className={styles.actionBar}>
                  <Button size="large" onClick={() => setSelectedDept(undefined)}>
                    重置
                  </Button>
                  <Button
                    size="large"
                    color="primary"
                    fill="solid"
                    onClick={() => {
                      setSearchParams(prev => ({ ...prev, page: 1, ownerOrgId: selectedDept, zoneIdList: [] }));

                      ref.current?.close();
                    }}
                  >
                    确定
                  </Button>
                </div>
              </div>
            </Dropdown.Item>
            <Dropdown.Item
              key="zoneIdList"
              title="区域名称"
              style={{ color: isEmpty(searchParams!.zoneIdList) ? '' : 'var(--adm-color-primary)' }}
            >
              <div className={styles.dropdownPanel}>
                <SearchBar placeholder="请输入区域名称" ref={searchZoneRef} onChange={setSearchAreaText} />
                {!isEmpty(filteredAreaItems) ? (
                  <CheckList
                    value={selectedArea}
                    multiple
                    onChange={val => {
                      setSelectedArea(val as number[]);
                    }}
                  >
                    {filteredAreaItems?.map(item => (
                      <CheckList.Item key={item.id} value={item.id!}>
                        {item.zoneName}
                      </CheckList.Item>
                    ))}
                  </CheckList>
                ) : (
                  <div className={styles.emptyState}>
                    <Empty
                      image={NoData}
                      description={searchParams?.ownerOrgId ? '暂无数据' : '暂无数据，请先选择部门'}
                    />
                  </div>
                )}
                <div className={styles.actionBar}>
                  <Button size="large" onClick={() => setSelectedArea([])}>
                    重置
                  </Button>
                  <Button
                    size="large"
                    color="primary"
                    fill="solid"
                    onClick={() => {
                      setSearchParams(prev => ({ ...prev, page: 1, zoneIdList: selectedArea }));
                      ref.current?.close();
                    }}
                  >
                    确定
                  </Button>
                </div>
              </div>
            </Dropdown.Item>
            <div className={styles.batchItem} onClick={toggleBatchMode}>
              <span>批量审批</span>
              <img src={unActualApprovalImg} width={8} alt="" style={{ marginLeft: 4 }} />
            </div>
            <Selector
              options={[
                {
                  label: (
                    <Badge content={approvalCountInfo?.todoNum} style={{ '--right': '-6px', '--top': '4px' }}>
                      我的待办
                    </Badge>
                  ),
                  value: 0,
                },
              ]}
              onChange={(arr, extend) => {
                ref.current?.close();
                setSearchParams(prev => ({ ...prev, page: 1, todoStatus: arr[0] }));
              }}
            />
          </Dropdown>

          <Tabs activeKey={activeTab} onChange={handleTabChange} className={styles.tabs}>
            <Tabs.Tab
              title={<Badge content={approvalCountInfo?.totalNum}>{DelayTitleDisplay[DelayTitleEnum.ALL]}</Badge>}
              key={DelayTitleEnum.ALL}
            />
            <Tabs.Tab
              title={<Badge content={approvalCountInfo?.draftNum}>{DelayTitleDisplay[DelayTitleEnum.DRAFT]}</Badge>}
              key={DelayTitleEnum.DRAFT}
            />
            <Tabs.Tab
              title={
                <Badge content={approvalCountInfo?.pendingApprovalNum}>
                  {DelayTitleDisplay[DelayTitleEnum.PENDING]}
                </Badge>
              }
              key={DelayTitleEnum.PENDING}
            />
            <Tabs.Tab
              title={
                <Badge content={approvalCountInfo?.approvedNum}>{DelayTitleDisplay[DelayTitleEnum.APPROVED]}</Badge>
              }
              key={DelayTitleEnum.APPROVED}
            />
            <Tabs.Tab
              title={<Badge content={approvalCountInfo?.rejectNum}>{DelayTitleDisplay[DelayTitleEnum.REJECTED]}</Badge>}
              key={DelayTitleEnum.REJECTED}
            />
          </Tabs>
        </div>
      )}

      {/* 批量审批模式下的顶部栏 */}
      {batchMode && (
        <div className={styles.batchHeader}>
          <div className={styles.batchSelectAll}>
            <Checkbox
              checked={selectAllStatus === 'all'}
              indeterminate={selectAllStatus === 'partial'}
              onChange={handleSelectAll}
            />
            <span>全选</span>
            <div className={styles.batchInfo}>
              已选中 <span style={{ color: 'red' }}>{selectedIds.length}</span>/{total} 项
            </div>
          </div>
          <Button size="small" fill="none" color="primary" onClick={toggleBatchMode}>
            退出批量审批
          </Button>
        </div>
      )}

      {/* 列表区域 */}
      <div className={styles.list}>
        {!isEmpty(data) ? (
          data!.map(item => {
            const isApprovable =
              (item.approvalResult === ApprovalStatus.PENDING_FIRST ||
                item.approvalResult === ApprovalStatus.PENDING_SECOND) &&
              item.approvable;
            const isSelected = selectedIds.includes(item.id!);

            return (
              <React.Fragment key={item.id}>
                <Card
                  className={styles.card}
                  onClick={() => {
                    if (batchMode && isApprovable) {
                      if (isSelected) {
                        setSelectedIds(prev => prev.filter(id => id !== item.id));
                      } else {
                        setSelectedIds(prev => [...prev, item.id!]);
                      }
                    } else if (!batchMode) {
                      navigate(
                        `/intelligent-platform/energyUse/application/detail/${item.id}?status=${item.approvalResult}&approvable=${item.approvable}`
                      );
                    }
                  }}
                >
                  <div className={styles.cardContent}>
                    {batchMode && (
                      <div className={styles.cardCheckbox}>
                        <Checkbox
                          checked={isSelected}
                          disabled={!isApprovable}
                          onClick={e => {
                            e.stopPropagation();
                            if (isApprovable) {
                              if (isSelected) {
                                setSelectedIds(prev => prev.filter(id => id !== item.id));
                              } else {
                                setSelectedIds(prev => [...prev, item.id!]);
                              }
                            }
                          }}
                        />
                      </div>
                    )}
                    <div className={styles.cardDetail}>
                      <div>
                        部门：<span className={styles.value}>{item.ownerOrgName}</span>
                        <Tag
                          color={ApprovalStatusColorDisplay[item.approvalResult as unknown as ApprovalStatus]}
                          style={{ float: 'right' }}
                        >
                          {ApprovalStatusDisplay[item.approvalResult as unknown as ApprovalStatus]}
                        </Tag>
                      </div>
                      <div>
                        区域：
                        <span className={styles.value}>{item.areaList?.map(a => a.zoneName).join('、') ?? '-'}</span>
                      </div>
                      <div>
                        申请人：
                        <span className={styles.value}>{item.applyUserName ?? '-'}</span>
                      </div>
                      <div>
                        申请时间：
                        <span className={styles.value}>
                          {item.applyTime ? dayjs(item.applyTime).format('YYYY-MM-DD HH:mm:ss') : '-'}
                        </span>
                      </div>
                      <div>
                        申请用能时间范围：
                        <span className={styles.value}>{item.timeRange ?? '-'}</span>
                      </div>
                      <div>
                        申请能源介质：
                        <span className={styles.value}>{item.energyMedium ?? '-'}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </React.Fragment>
            );
          })
        ) : (
          <div className={styles.emptyState}>
            <Empty image={NoData} description="暂无数据" />
          </div>
        )}
        <InfiniteScroll loadMore={loadMore} hasMore={hasMore} />
      </div>

      {/* 批量审批模式下的底部操作栏 */}
      {batchMode && (
        <div className={styles.batchActionBar}>
          <Button size="large" onClick={() => handleBatchApproval(0)}>
            批量通过
          </Button>
          <Button size="large" color="danger" onClick={() => handleBatchApproval(1)}>
            批量驳回
          </Button>
        </div>
      )}
    </div>
  );
};

export default EnergyUseApplication;
